import { defineComponent, onMounted, ref } from 'vue'
import { getHallList } from '../halls/api'
import { getRecommendHallList, saveRecommendHall } from './api'

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 20, total_pages: 1 }
  }
  const total = Number(payload.total || 0)
  const page = Number(payload.page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 20)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 20,
    total_pages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1
  }
}

function pickHall(row) {
  return row && row.hall && typeof row.hall === 'object' ? row.hall : {}
}

function pickHallId(row) {
  if (!row || typeof row !== 'object') return ''
  const id = row.hall_id ?? row.hallId ?? pickHall(row).id
  return id == null ? '' : String(id)
}

function pickHallName(row) {
  const hall = pickHall(row)
  const name = hall.hallName ?? hall.hall_name ?? hall.name
  return name != null && String(name).trim() !== '' ? String(name) : '—'
}

function pickHallAlias(row) {
  const hall = pickHall(row)
  const alias = hall.hallAlias ?? hall.hall_alias
  return alias != null && String(alias).trim() !== '' ? String(alias) : '—'
}

function pickHallSurname(row) {
  const hall = pickHall(row)
  const surname = hall.surname ?? hall.clan
  return surname != null && String(surname).trim() !== '' ? String(surname) : '—'
}

function pickHallCover(row) {
  const hall = pickHall(row)
  const cover = hall.coverimg ?? hall.cover_img ?? hall.cover_url
  return cover != null && String(cover).trim() !== '' ? String(cover) : ''
}

function pickHallMemberCount(row) {
  const hall = pickHall(row)
  const count = hall.memberCount ?? hall.member_count
  return count != null ? count : '—'
}

function pickStateLabel(state) {
  if (state === 1 || state === '1') return '推荐中'
  if (state === 0 || state === '0') return '已取消'
  return '—'
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).replace('T', ' ').slice(0, 19)
}

export default defineComponent({
  name: 'RecommendHallsView',
  setup() {
    const recommendList = ref([])
    const loading = ref(false)
    const saving = ref(false)

    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalPages = ref(1)
    const total = ref(0)
    const filterState = ref('')

    const modalVisible = ref(false)
    const selectedHall = ref(null)
    const hallSearchQuery = ref('')
    const hallSearchResults = ref([])
    const hallSearchLoading = ref(false)
    const hallDropdownOpen = ref(false)

    let hallSearchTimer = null

    const fetchList = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        }
        if (filterState.value !== '') params.state = Number(filterState.value)
        const response = await getRecommendHallList(params)
        recommendList.value = extractList(response)
        const meta = extractMeta(response)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = meta.total_pages
        total.value = meta.total
      } catch (error) {
        console.error('获取推荐祠堂列表失败:', error)
        recommendList.value = []
        alert(error.message || '获取推荐祠堂列表失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const searchHalls = async () => {
      const keyword = String(hallSearchQuery.value || '').trim()
      if (!keyword) {
        hallSearchResults.value = []
        return
      }

      hallSearchLoading.value = true
      try {
        const response = await getHallList({
          page: 1,
          page_size: 20,
          keyword
        })
        const payload = response.data !== undefined ? response.data : response
        hallSearchResults.value = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : []
      } catch (error) {
        console.error('搜索祠堂失败:', error)
        hallSearchResults.value = []
      } finally {
        hallSearchLoading.value = false
      }
    }

    const handleHallSearch = () => {
      hallDropdownOpen.value = true
      if (hallSearchTimer) clearTimeout(hallSearchTimer)
      hallSearchTimer = setTimeout(searchHalls, 300)
    }

    const selectHall = (hall) => {
      selectedHall.value = hall
      hallSearchQuery.value = hall.hallName ?? hall.hall_name ?? hall.name ?? ''
      hallDropdownOpen.value = false
      hallSearchResults.value = []
    }

    const clearSelectedHall = () => {
      selectedHall.value = null
      hallSearchQuery.value = ''
      hallSearchResults.value = []
    }

    const closeHallDropdown = () => {
      hallDropdownOpen.value = false
    }

    const openCreateModal = () => {
      clearSelectedHall()
      modalVisible.value = true
    }

    const closeModal = () => {
      if (saving.value) return
      modalVisible.value = false
      clearSelectedHall()
    }

    const handleAddRecommend = async () => {
      const hallId = selectedHall.value?.id ?? selectedHall.value?.hall_id ?? selectedHall.value?.hallId
      if (hallId == null || hallId === '') {
        alert('请选择祠堂')
        return
      }

      saving.value = true
      try {
        await saveRecommendHall({ hall_id: Number(hallId), state: 1 })
        alert('添加推荐成功')
        closeModal()
        await fetchList()
      } catch (error) {
        console.error('添加推荐祠堂失败:', error)
        alert(error.message || '添加推荐失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handleToggleState = async (row, state) => {
      const hallId = pickHallId(row)
      if (!hallId) {
        alert('缺少祠堂 ID')
        return
      }

      const actionText = state === 1 ? '重新推荐' : '取消推荐'
      const hallName = pickHallName(row)
      if (!confirm(`确定要${actionText}「${hallName}」吗？`)) return

      saving.value = true
      try {
        await saveRecommendHall({ hall_id: Number(hallId), state })
        alert(`${actionText}成功`)
        await fetchList()
      } catch (error) {
        console.error(`${actionText}失败:`, error)
        alert(error.message || `${actionText}失败，请稍后重试`)
      } finally {
        saving.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchList()
    }

    const handleStateFilterChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const resetFilters = () => {
      filterState.value = ''
      currentPage.value = 1
      fetchList()
    }

    onMounted(fetchList)

    return {
      recommendList,
      loading,
      saving,
      currentPage,
      totalPages,
      total,
      filterState,
      modalVisible,
      selectedHall,
      hallSearchQuery,
      hallSearchResults,
      hallSearchLoading,
      hallDropdownOpen,
      pickHallId,
      pickHallName,
      pickHallAlias,
      pickHallSurname,
      pickHallCover,
      pickHallMemberCount,
      pickStateLabel,
      formatDate,
      openCreateModal,
      closeModal,
      handleHallSearch,
      selectHall,
      clearSelectedHall,
      closeHallDropdown,
      handleAddRecommend,
      handleToggleState,
      handlePageChange,
      handleStateFilterChange,
      resetFilters
    }
  }
})
